<script setup>
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';

import { api } from '@/api/client.js';
import BarRankRow from '@/components/panels/BarRankRow.vue';
import ListPager from '@/components/panels/ListPager.vue';
import PanelCard from '@/components/panels/PanelCard.vue';
import { useListPage } from '@/composables/useListPage.js';
import { usePanelState } from '@/composables/usePanelState.js';
import { usePoll } from '@/composables/usePoll.js';
import { useTenantStore } from '@/stores/tenant.js';
import { TOP_PAGES_EMPTY } from '@/utils/emptyStateCopy.js';

const tenant = useTenantStore();
const { activeAppId, timeWindow } = storeToRefs(tenant);
const ps = usePanelState('loading');
const data = ref({ total: 0, rows: [] });

async function fetchFn({ signal }) {
  if (!activeAppId.value) return;
  return api.events.topPages({ appId: activeAppId.value, window: timeWindow.value, signal });
}

const { tick, status: refreshStatus, lastUpdatedAt } = usePoll(fetchFn, {
  watch: [() => tenant.changeKey],
  onSuccess(result) {
    if (!result || result.ok !== true) return;
    data.value = { total: result.total ?? 0, rows: result.rows ?? [] };
    ps.setState(data.value.rows.length === 0 ? 'empty' : 'data');
  },
  onError(err) {
    ps.setState('error', {
      message: "Couldn't load top pages",
      detail: err?.message ?? '',
    });
  },
});

ps.setRetryHandler(() => tick());

// Exponer ps para los panelRefs del LandingView (error global / retry all).
defineExpose({ ps, refreshStatus });

const rows = computed(() => data.value.rows.map((row) => ({
  ...row,
  pct: data.value.total > 0 ? (row.count / data.value.total) * 100 : null,
})));
const { page, visible } = useListPage(rows, 5);
watch(() => tenant.changeKey, () => { page.value = 1; });
</script>

<template>
  <PanelCard
    title="Top pages"
    help-key="topPages"
    :state="ps.state.value"
    :empty-headline="TOP_PAGES_EMPTY.headline"
    :empty-body="TOP_PAGES_EMPTY.body"
    :error-message="ps.errorMessage.value"
    :error-detail="ps.errorDetail.value"
    :on-retry="ps.retry"
    :refresh-status="refreshStatus"
    :last-updated-at="lastUpdatedAt"
  >
    <div class="top-pages">
      <p class="top-pages__summary">
        <strong>{{ data.total.toLocaleString() }}</strong> pageviews in this period
      </p>
      <div class="top-pages__labels">
        <span>Page</span><span>Views · share of all pageviews</span>
      </div>
      <BarRankRow
        v-for="row in visible"
        :key="row.pagePath"
        :label="row.pagePath"
        :count="row.count"
        :pct="row.pct"
        mono
      />
      <ListPager
        v-model:page="page"
        :total="rows.length"
        :page-size="5"
        label="Top pages pagination"
      />
    </div>
    <template
      v-if="rows.length"
      #tableFallback
    >
      <table class="tp-table">
        <caption class="visually-hidden">
          Top pages by pageviews in this window
        </caption>
        <thead>
          <tr>
            <th scope="col">
              Page path
            </th>
            <th
              scope="col"
              class="numeric"
            >
              Pageviews
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="`t-${row.pagePath}`"
          >
            <td>{{ row.pagePath }}</td>
            <td class="numeric">
              {{ row.count }}
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </PanelCard>
</template>

<style scoped>
.top-pages {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.top-pages__summary {
  margin: 0 0 var(--space-2);
  color: var(--fg-secondary);
  font-size: var(--text-sm);
}

.top-pages__summary strong {
  color: var(--fg-primary);
  font-size: var(--text-2xl);
  font-variant-numeric: tabular-nums;
}

.top-pages__labels {
  display: flex;
  justify-content: space-between;
  gap: var(--space-3);
  font-size: var(--text-xs);
  color: var(--fg-secondary);
}

.tp-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-xs);
}

.tp-table th,
.tp-table td {
  padding: var(--space-1) var(--space-2);
  text-align: left;
  border-bottom: var(--bw-thin) solid var(--border-default);
}

.tp-table th {
  color: var(--fg-secondary);
  font-weight: var(--fw-medium);
}

.tp-table .numeric {
  text-align: right;
  font-feature-settings: "tnum";
}
</style>
